import { useCallback, useEffect, useMemo, useState } from 'react';
import { endOfDay, startOfDay } from 'date-fns';
import { debounce } from 'lodash';

import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { WorkflowStatus } from 'models/Workflow';
import { portalConnectionQueryKeys } from 'services/api/portalConnections';
import { indexWorkflows, WorkflowListParams, workflowQueryKeys } from 'services/api/workflows';

import { PORTAL_AUTOMATION_API_NAME } from '../constants';

import { hasServerFilters, NO_TASK_SERVER_FILTERS, TASK_STATUS_PARAM, TaskServerFilters, TaskSort } from './taskCards';

const NUDGE_DEBOUNCE_MS = 400;
const NUDGE_MAX_WAIT_MS = 1500;

// A backstop for a dropped socket, not a refresh cadence — the nudge is how this list stays
// live. Deliberately long: the whole page is a projection over `action_required`, so a poll
// that fires while nothing settled re-fetches a list that cannot have changed.
const FALLBACK_POLL_INTERVAL_MS = 300_000;

/**
 * The attention rule, expressed as a query: `portal_automation` runs in `action_required`,
 * and nothing else. This list is not a run monitor — a run in flight requires nobody's
 * attention, so it is not here, which is why no "running vs. waiting on you" indicator exists.
 *
 * Filtered SERVER-side (the index takes both parameters) rather than fetched whole and sieved:
 * the unfiltered index is lifetime-scoped and terminal-inclusive, so a client with a year of
 * referrals would ship megabytes to render a handful of cards. The status and created-date
 * filters ride the same request for the same reason; only source/reason sieve client-side.
 */
const TASK_LIST_PARAMS: WorkflowListParams = {
  definitionApiName: [PORTAL_AUTOMATION_API_NAME],
  status: [WorkflowStatus.ACTION_REQUIRED],
};

// Twenty per fetch, more as the person scrolls (MoreContentRequester). The sort is SERVER-side
// for the same reason the list pages at all: with pages in play a client-side sort could only
// reorder what happens to be loaded, and "oldest" would show the oldest of page one.
const TASK_PAGE_SIZE = 20;
export const SORT_BY: Record<TaskSort, string> = { newest: 'created_at desc', oldest: 'created_at asc' };

// Open-ended bounds for a half-set range — the serializer wants the 2-tuple.
const RANGE_FLOOR = new Date('1900-01-01T00:00:00Z');
const RANGE_CEILING = new Date('2999-01-01T00:00:00Z');

function serverParams({ statuses, createdAt }: TaskServerFilters): WorkflowListParams {
  const params: WorkflowListParams = {};
  if (statuses.length > 0) params.listStatus = statuses.map((status) => TASK_STATUS_PARAM[status]);
  if (createdAt.start || createdAt.end) {
    params.createdAt = [
      createdAt.start ? startOfDay(createdAt.start) : RANGE_FLOOR,
      createdAt.end ? endOfDay(createdAt.end) : RANGE_CEILING,
    ];
  }
  return params;
}

export default function useTasks(sort: TaskSort = 'newest', server: TaskServerFilters = NO_TASK_SERVER_FILTERS) {
  const queryClient = useQueryClient();
  const [subscriptionRejected, setSubscriptionRejected] = useState(false);
  const [everConnected, setEverConnected] = useState(false);

  const params: WorkflowListParams = { ...TASK_LIST_PARAMS, sortBy: SORT_BY[sort], ...serverParams(server) };
  const query = useInfiniteQuery({
    // Sort is part of the key: flipping it is a different list, refetched from page one. Still
    // under the shared `lists` prefix, so the nudge's invalidation reaches every variant.
    queryKey: workflowQueryKeys.list(params),
    queryFn: ({ pageParam, signal }) =>
      indexWorkflows({ ...params, page: pageParam, pageSize: TASK_PAGE_SIZE }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPageParam < lastPage.meta.totalPages ? lastPageParam + 1 : undefined,
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
  });

  const workflows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  // The tab label counts the whole population, so a server-side filter needs a second,
  // one-row fetch for the unfiltered total. Same `lists` prefix, so the nudge refreshes it too.
  const filtered = hasServerFilters(server);
  const countParams: WorkflowListParams = { ...TASK_LIST_PARAMS, page: 1, pageSize: 1 };
  const countQuery = useQuery({
    queryKey: workflowQueryKeys.list(countParams),
    queryFn: ({ signal }) => indexWorkflows(countParams, signal),
    enabled: filtered,
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
  });

  // One subscription for the page — never one per card. WorkflowChannel does a policy-scoped
  // lookup per subscription, so a page of thirty cards would cost thirty DB reads on load for
  // information this client-wide nudge already carries.
  // Both projections the page draws from, not just the cards. The banner and the operational
  // cards read connections, so leaving that query out left a sweep that started elsewhere
  // unannounced and a finished one banner-ed until remount — `refetchOnWindowFocus` is off
  // globally, so nothing else would have refreshed it.
  const nudged = useMemo(
    () =>
      debounce(
        () => {
          queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
          queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.base });
        },
        NUDGE_DEBOUNCE_MS,
        { maxWait: NUDGE_MAX_WAIT_MS }
      ),
    [queryClient]
  );
  useEffect(() => () => nudged.cancel(), [nudged]);

  // The nudge is contentless and client-wide: any run settling anywhere wakes this page. The
  // filtered fetch is what keeps that affordable.
  const { isConnected } = useWebsocketSubscription({
    channel: 'WorkflowListChannel',
    onReceived: () => nudged(),
    // A rejected subscription is otherwise silent, and a silently dead socket looks exactly
    // like a page with no work on it. Surfacing it lets the page say it may be stale.
    onRejected: useCallback(() => setSubscriptionRejected(true), []),
  });

  useEffect(() => {
    if (isConnected) setEverConnected(true);
  }, [isConnected]);

  return {
    workflows,
    // The whole population, not just the loaded pages — the tab label counts what exists.
    totalRecords: filtered ? (countQuery.data?.meta.totalRecords ?? 0) : (query.data?.pages[0]?.meta.totalRecords ?? 0),
    // The infinite-query handle itself, for MoreContentRequester's scroll sentinel.
    query,
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
    // Live means the nudge is arriving. False falls back to the slow poll, which is worth
    // telling the reader about — this surface promises cards that appear and vanish on their own.
    isLive: isConnected && !subscriptionRejected,
    // What the page actually renders on. `isConnected` is false while the socket is still
    // opening, so "not live" on first paint means "connecting", not "stale" — saying so then
    // would put a warning on every single load. A rejection is known bad immediately; a drop
    // is only knowable once there was something to drop.
    isStale: subscriptionRejected || (everConnected && !isConnected),
  };
}
